"use client";

import { useAuth } from "@clerk/nextjs";
import { useMutation, useQuery } from "@tanstack/react-query";

import { getQueryClient } from "@/app/providers";
import { useAuthedApi } from "@/lib/api/useAuthedApi";
import {
  UserInfoResponse,
  UserTranscriptionSpecificResponse,
  UserTranslationSpecificResponse,
  UserUploadedFilesResponse,
} from "@/models/user.model";

import {
  copyUserExampleTranscription,
  deleteUserSpecificTranscription,
  deleteUserSpecificTranslation,
  deleteUserUploadedFile,
  generateUserApiKey,
  getUserInfo,
  getUserSpecificTranscription,
  getUserSpecificTranslation,
  getUserTranscriptionList,
  getUserTranslationList,
  getUserUploadedFiles,
  renameUserFile,
  saveUserSpecificTranscription,
  saveUserSpecificTranslation,
  toggleTranscriptionShareable,
  updateUserFileSort,
  updateUserFilesPerPage,
  updateUserLocale,
} from "../fetch/user.fetch";
import { queryKeys } from "./keys.query";

export function useUserInfoQuery() {
  const { userId } = useAuth();
  const api = useAuthedApi();

  return useQuery<UserInfoResponse | null>({
    queryKey: [queryKeys.userInfo, userId],
    queryFn: () => getUserInfo(api),
    enabled: !!userId,
  });
}

export function isPaidSubscriptionStatus(status?: UserInfoResponse["subscription_status"] | null) {
  return Boolean(status) && status !== "free";
}

/** Returns true = paid, false = free, null = still loading */
export function useIsPro(): boolean | null {
  const { data: userInfo } = useUserInfoQuery();
  if (!userInfo) return null;
  return isPaidSubscriptionStatus(userInfo.subscription_status);
}

export function useUserTranscriptionListQuery() {
  const { userId } = useAuth();
  const api = useAuthedApi();

  return useQuery({
    queryKey: [queryKeys.userTranscriptionList, userId],
    queryFn: () => getUserTranscriptionList(api),
    enabled: !!userId,
  });
}

export function useUserSpecificTranscriptionQuery(uuid_filename: string, enabled: boolean = true) {
  const { userId } = useAuth();
  const api = useAuthedApi();

  return useQuery({
    queryKey: [queryKeys.userSpecificTranscription, uuid_filename, userId],
    queryFn: () => getUserSpecificTranscription(uuid_filename, api, !!userId),
    enabled: enabled && !!uuid_filename && !!userId,
    retry: false,
  });
}

export function useUserTranslationListQuery() {
  const { userId } = useAuth();
  const api = useAuthedApi();

  return useQuery({
    queryKey: [queryKeys.userTranslationList, userId],
    queryFn: () => getUserTranslationList(api),
    enabled: !!userId,
  });
}

export function useUserUploadedFilesQuery() {
  const { userId } = useAuth();
  const api = useAuthedApi();

  return useQuery({
    queryKey: [queryKeys.userUploadedFiles, userId],
    queryFn: () => getUserUploadedFiles(api),
    enabled: !!userId,
  });
}

export function useDeleteUserFileMutation() {
  const { userId } = useAuth();
  const api = useAuthedApi();
  const queryClient = getQueryClient();

  return useMutation({
    mutationKey: [queryKeys.deleteUserUploadedFile],
    mutationFn: (uuid_filename: string) => deleteUserUploadedFile(uuid_filename, api),
    onMutate: async uuid_filename => {
      await queryClient.cancelQueries({
        queryKey: [queryKeys.userUploadedFiles, userId],
      });

      const previousFiles = queryClient.getQueryData<UserUploadedFilesResponse>([queryKeys.userUploadedFiles, userId]);

      queryClient.setQueryData<UserUploadedFilesResponse>([queryKeys.userUploadedFiles, userId], prev =>
        prev
          ? {
              ...prev,
              files: prev.files.filter(file => file.uuid_filename !== uuid_filename),
            }
          : undefined
      );

      return { previousFiles };
    },
    onError: (_error, _uuidFilename, context) => {
      if (context?.previousFiles) {
        queryClient.setQueryData<UserUploadedFilesResponse>([queryKeys.userUploadedFiles, userId], context.previousFiles);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.userUploadedFiles, userId],
      });
    },
  });
}

export function useUserSpecificTranslationQuery(uuid_filename: string) {
  const { userId } = useAuth();
  const api = useAuthedApi();
  console.log("in query" + uuid_filename);

  return useQuery({
    queryKey: [queryKeys.userSpecificTranslation, uuid_filename, userId],
    queryFn: () => getUserSpecificTranslation(uuid_filename, api, !!userId),
    enabled: !!uuid_filename,
    retry: false,
  });
}

export function useUserSpecificTranslationMutation() {
  const api = useAuthedApi();

  return useMutation({
    mutationKey: [queryKeys.userSpecificTranslationMutation],
    mutationFn: (uuid_filename: string) => getUserSpecificTranslation(uuid_filename, api),
  });
}

export function useUserSpecificTranslationDeleteMutation(uuid_filename: string) {
  const api = useAuthedApi();
  return useMutation({
    mutationKey: [queryKeys.userSpecificTranslationDeleteMutation],
    mutationFn: (body: BodyInit) => deleteUserSpecificTranslation(body, api),
    onSuccess: () => {
      const queryClient = getQueryClient();
      queryClient.invalidateQueries({
        queryKey: [queryKeys.userSpecificTranslation, uuid_filename],
      });
    },
  });
}

export function useUserSpecificTranscriptionDeleteMutation() {
  const api = useAuthedApi();

  return useMutation({
    mutationKey: [queryKeys.userSpecificTranscriptionDelete],
    mutationFn: (body: BodyInit) => deleteUserSpecificTranscription(body, api),
    onSuccess: () => {
      const queryClient = getQueryClient();
      queryClient.invalidateQueries({
        queryKey: [queryKeys.userTranscriptionList],
      });
    },
  });
}

export function useUserSpecificTranscriptionSaveMutation(uuid_filename: string) {
  const api = useAuthedApi();

  return useMutation({
    mutationKey: [queryKeys.userSpecificTranscriptionSave],
    mutationFn: (body: BodyInit) => saveUserSpecificTranscription(body, api),
    onSuccess: () => {
      const queryClient = getQueryClient();
      queryClient.invalidateQueries({
        queryKey: [queryKeys.userSpecificTranscription, uuid_filename],
      });
    },
  });
}

export function useUserSpecificTranslationSaveMutation(uuid_filename: string) {
  const api = useAuthedApi();

  return useMutation({
    mutationKey: [queryKeys.userSpecificTranslationSave],
    mutationFn: (body: BodyInit) => saveUserSpecificTranslation(body, api),
    onSuccess: () => {
      const queryClient = getQueryClient();
      queryClient.invalidateQueries({
        queryKey: [queryKeys.userSpecificTranslation, uuid_filename],
      });
    },
  });
}

export function useUserExampleTranscriptionMutation() {
  const { userId } = useAuth();
  const api = useAuthedApi();

  return useMutation({
    mutationKey: [queryKeys.userExampleTranscription],
    mutationFn: () => copyUserExampleTranscription(api),
    onSuccess: () => {
      const queryClient = getQueryClient();
      queryClient.invalidateQueries({
        queryKey: [queryKeys.userUploadedFiles, userId],
      });
    },
  });
}

export function useUserApiKeyMutation() {
  const api = useAuthedApi();

  return useMutation({
    mutationKey: [queryKeys.generateUserApiKey],
    mutationFn: () => generateUserApiKey(api),
  });
}

export function useUserLocaleUpdateMutation() {
  const { userId } = useAuth();
  const api = useAuthedApi();
  const queryClient = getQueryClient();

  return useMutation({
    mutationKey: [queryKeys.updateUserLocale, userId],
    mutationFn: (payload: { locale: string }) => updateUserLocale(JSON.stringify(payload), api),
    onSuccess: data => {
      queryClient.setQueryData<UserInfoResponse>([queryKeys.userInfo, userId], prev =>
        prev
          ? {
              ...prev,
              locale: data.locale,
            }
          : undefined
      );
    },
  });
}

export function useUserFileSortUpdateMutation() {
  const { userId } = useAuth();
  const api = useAuthedApi();
  const queryClient = getQueryClient();

  return useMutation({
    mutationKey: [queryKeys.updateUserFileSort, userId],
    mutationFn: (payload: { file_sort: UserInfoResponse["file_sort"] }) => updateUserFileSort(JSON.stringify(payload), api),
    onMutate: async payload => {
      await queryClient.cancelQueries({ queryKey: [queryKeys.userInfo, userId] });

      const previousUserInfo = queryClient.getQueryData<UserInfoResponse | null>([queryKeys.userInfo, userId]);

      queryClient.setQueryData<UserInfoResponse | null>([queryKeys.userInfo, userId], prev =>
        prev
          ? {
              ...prev,
              file_sort: payload.file_sort,
            }
          : prev
      );

      return { previousUserInfo };
    },
    onSuccess: data => {
      queryClient.setQueryData<UserInfoResponse | null>([queryKeys.userInfo, userId], prev =>
        prev
          ? {
              ...prev,
              file_sort: data.file_sort,
            }
          : prev
      );
    },
    onError: (_error, _payload, context) => {
      if (typeof context?.previousUserInfo !== "undefined") {
        queryClient.setQueryData<UserInfoResponse | null>([queryKeys.userInfo, userId], context.previousUserInfo);
      }
    },
  });
}

export function useUserFilesPerPageUpdateMutation() {
  const { userId } = useAuth();
  const api = useAuthedApi();
  const queryClient = getQueryClient();

  return useMutation({
    mutationKey: [queryKeys.updateUserFilesPerPage, userId],
    mutationFn: (payload: { files_per_page: UserInfoResponse["files_per_page"] }) => updateUserFilesPerPage(JSON.stringify(payload), api),
    onMutate: async payload => {
      await queryClient.cancelQueries({ queryKey: [queryKeys.userInfo, userId] });

      const previousUserInfo = queryClient.getQueryData<UserInfoResponse | null>([queryKeys.userInfo, userId]);

      queryClient.setQueryData<UserInfoResponse | null>([queryKeys.userInfo, userId], prev =>
        prev
          ? {
              ...prev,
              files_per_page: payload.files_per_page,
            }
          : prev
      );

      return { previousUserInfo };
    },
    onSuccess: data => {
      queryClient.setQueryData<UserInfoResponse | null>([queryKeys.userInfo, userId], prev =>
        prev
          ? {
              ...prev,
              files_per_page: data.files_per_page,
            }
          : prev
      );
    },
    onError: (_error, _payload, context) => {
      if (typeof context?.previousUserInfo !== "undefined") {
        queryClient.setQueryData<UserInfoResponse | null>([queryKeys.userInfo, userId], context.previousUserInfo);
      }
    },
  });
}

export function useUserFileRenameMutation(uuid_filename: string) {
  const { userId } = useAuth();
  const api = useAuthedApi();
  const queryClient = getQueryClient();

  return useMutation({
    mutationKey: [queryKeys.renameUserFile],
    mutationFn: (body: { new_name: string }) => renameUserFile(uuid_filename, JSON.stringify(body), api),
    onMutate: async body => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [queryKeys.userUploadedFiles, userId] }),
        queryClient.cancelQueries({ queryKey: [queryKeys.userSpecificTranscription, uuid_filename, userId] }),
        queryClient.cancelQueries({ queryKey: [queryKeys.userSpecificTranslation, uuid_filename, userId] }),
      ]);

      const previousFiles = queryClient.getQueryData<UserUploadedFilesResponse>([queryKeys.userUploadedFiles, userId]);
      const previousTranscription = queryClient.getQueryData<UserTranscriptionSpecificResponse>([
        queryKeys.userSpecificTranscription,
        uuid_filename,
        userId,
      ]);
      const previousTranslation = queryClient.getQueryData<UserTranslationSpecificResponse>([
        queryKeys.userSpecificTranslation,
        uuid_filename,
        userId,
      ]);

      queryClient.setQueryData<UserUploadedFilesResponse>([queryKeys.userUploadedFiles, userId], prev =>
        prev
          ? {
              ...prev,
              files: prev.files.map(f => (f.uuid_filename === uuid_filename ? { ...f, original_filename: body.new_name } : f)),
            }
          : undefined
      );

      queryClient.setQueryData<UserTranscriptionSpecificResponse>([queryKeys.userSpecificTranscription, uuid_filename, userId], prev =>
        prev
          ? {
              ...prev,
              original_filename: body.new_name,
            }
          : undefined
      );

      queryClient.setQueryData<UserTranslationSpecificResponse>([queryKeys.userSpecificTranslation, uuid_filename, userId], prev =>
        prev
          ? {
              ...prev,
              original_filename: body.new_name,
            }
          : undefined
      );

      return { previousFiles, previousTranscription, previousTranslation };
    },
    onSuccess: data => {
      queryClient.setQueryData<UserUploadedFilesResponse>([queryKeys.userUploadedFiles, userId], prev =>
        prev
          ? {
              ...prev,
              files: prev.files.map(f => (f.uuid_filename === uuid_filename ? { ...f, original_filename: data.new_name } : f)),
            }
          : undefined
      );

      queryClient.setQueryData<UserTranscriptionSpecificResponse>([queryKeys.userSpecificTranscription, uuid_filename, userId], prev =>
        prev
          ? {
              ...prev,
              original_filename: data.new_name,
            }
          : undefined
      );

      queryClient.setQueryData<UserTranslationSpecificResponse>([queryKeys.userSpecificTranslation, uuid_filename, userId], prev =>
        prev
          ? {
              ...prev,
              original_filename: data.new_name,
            }
          : undefined
      );
    },
    onError: (_error, _body, context) => {
      if (context?.previousFiles) {
        queryClient.setQueryData<UserUploadedFilesResponse>([queryKeys.userUploadedFiles, userId], context.previousFiles);
      }

      if (context?.previousTranscription) {
        queryClient.setQueryData<UserTranscriptionSpecificResponse>(
          [queryKeys.userSpecificTranscription, uuid_filename, userId],
          context.previousTranscription
        );
      }

      if (context?.previousTranslation) {
        queryClient.setQueryData<UserTranslationSpecificResponse>(
          [queryKeys.userSpecificTranslation, uuid_filename, userId],
          context.previousTranslation
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.userUploadedFiles, userId],
      });
    },
  });
}

export function useUserTranscriptionShareableMutation() {
  const api = useAuthedApi();

  return useMutation({
    mutationKey: [queryKeys.userSpecificTranscriptionShareable],
    mutationFn: (payload: { uuid_filename: string; is_shareable: boolean }) =>
      toggleTranscriptionShareable(payload.uuid_filename, payload.is_shareable, api),
  });
}
